import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { EmptyState, Skeleton, PageContainer, Card, CardHeader, Button } from '../components/ui';
import { getDashboard, getSimulations } from '../api/api';

const STEP_KEY_BY_TYPE_AND_ORDER = {
  PHISHING: { 1: 'activityStepPhishing1', 2: 'activityStepPhishing2', 3: 'activityStepPhishing3', 4: 'activityStepPhishing4' },
  SOCIAL_ENGINEERING: { 1: 'activityStepSE1', 2: 'activityStepSE2', 3: 'activityStepSE3', 4: 'activityStepSE4' },
  BRUTE_FORCE: { 1: 'activityStepBrute1', 2: 'activityStepBrute2', 3: 'activityStepBrute3', 4: 'activityStepBrute4' },
};

const RISK_BADGE_STYLES = {
  LOW: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-500/15 dark:text-yellow-400 dark:border-yellow-500/30',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-500/15 dark:text-orange-400 dark:border-orange-500/30',
  CRITICAL: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30',
};

function parseApiDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return new Date(value);

  const hasExplicitTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(value);
  if (hasExplicitTimezone) return new Date(value);

  const localCandidate = new Date(value);
  const utcCandidate = new Date(`${value}Z`);

  if (Number.isNaN(localCandidate.getTime())) return utcCandidate;
  if (Number.isNaN(utcCandidate.getTime())) return localCandidate;

  const deltaMs = localCandidate.getTime() - Date.now();
  if (deltaMs > 2 * 60 * 60 * 1000) return utcCandidate;
  return localCandidate;
}

function formatTime(dateStr, locale = 'pt-BR') {
  if (!dateStr) return '';
  const parsed = parseApiDate(dateStr);
  if (!parsed || Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateTime(dateStr, locale = 'pt-BR') {
  if (!dateStr) return '';
  const parsed = parseApiDate(dateStr);
  if (!parsed || Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getRiskLevelFromScore(score) {
  if (score == null || score >= 80) return 'LOW';
  if (score >= 50) return 'MEDIUM';
  return 'HIGH';
}

function getRiskScoreColor(score) {
  if (score == null || score <= 30) return 'text-green-600 dark:text-green-400';
  if (score <= 60) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

const VULN_QUESTION_TO_KEY = {
  'A organização utiliza autenticação MFA (Multi-Fator)?': 'vulnQuestionMFA',
  'O login é monitorado?': 'vulnQuestionLogin',
  'O acesso a dados sensíveis é controlado?': 'vulnQuestionSensitiveData',
  'Existe processo de resposta a incidentes?': 'vulnQuestionIncidents',
  'Dados sensíveis são criptografados?': 'vulnQuestionEncryption',
  'Acesso remoto é protegido com VPN?': 'vulnQuestionVPN',
};

const VULN_RECOMMENDATION_PT = 'Implemente as medidas necessárias para atender este requisito de segurança.';
const VULN_TITLE_PREFIX_PT = 'Risco crítico: ';

export default function Dashboard() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const locale = (language === 'en-US' || language === 'en') ? 'en-US' : 'pt-BR';
  const RISK_LABELS = { LOW: t.low, MEDIUM: t.medium, HIGH: t.high, CRITICAL: t.critical };
  const SIM_LABELS = { PHISHING: t.phishing, SOCIAL_ENGINEERING: t.socialEngineering, BRUTE_FORCE: t.bruteForce };
  const getStepLabel = (simType, step) => {
    if (simType === 'BRUTE_FORCE') return step?.description || '';
    const key = STEP_KEY_BY_TYPE_AND_ORDER[simType]?.[step?.stepOrder];
    return (key && t[key]) || step?.description || '';
  };

  const [data, setData] = useState(null);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([getDashboard(organizationId), getSimulations(organizationId)])
      .then(([dash, sims]) => {
        setData(dash);
        setSimulations(sims || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [organizationId]);

  if (loading) {
    return (
      <Layout>
        <PageContainer className="animate-fade-in">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-10 w-20" />
              </Card>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <Skeleton className="h-6 w-48 mb-4" />
              <Skeleton className="h-32 w-full" />
            </Card>
            <Card className="p-6">
              <Skeleton className="h-6 w-48 mb-4" />
              <Skeleton className="h-24 w-full" />
            </Card>
          </div>
        </PageContainer>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <PageContainer>
          <div className="rounded-[10px] bg-red-500/10 border border-red-500/30 px-4 py-3 text-red-600 dark:text-red-400">
            {error}
          </div>
        </PageContainer>
      </Layout>
    );
  }

  const awarenessScore = data?.socialEngineeringAwarenessScore ?? data?.securityScore ?? null;
  const fallbackHumanRisk = awarenessScore != null ? 100 - awarenessScore : 50;
  const humanRiskScore = data?.humanRiskScore ?? fallbackHumanRisk;
  const totalSimulations = data?.totalSimulationsCount ?? 0;
  const vulnerabilidadesCount = data?.criticalVulnerabilitiesCount ?? 0;

  const phishingRiskPct = data?.phishingRiskPct ?? humanRiskScore;
  const seRiskPct = data?.socialEngineeringRiskPct ?? humanRiskScore;
  const authorityRiskPct = data?.authorityFraudRiskPct ?? humanRiskScore;

  // Build activity timeline from simulations
  const activityEvents = [];
  simulations.slice(0, 10).forEach((sim) => {
    const baseTime = parseApiDate(sim.executedAt)?.getTime();
    if (baseTime == null || Number.isNaN(baseTime)) return;
    activityEvents.push({
      time: baseTime,
      label: `${t.simulationComplete} - ${SIM_LABELS[sim.simulationType] || sim.simulationType}`,
      type: sim.simulationType,
      icon: 'simulation',
    });
    sim.steps?.forEach((step, i) => {
      activityEvents.push({
        time: baseTime + (i + 1) * 60000,
        type: sim.simulationType,
        icon: 'step',
        step,
      });
    });
  });
  activityEvents.sort((a, b) => b.time - a.time);
  const recentActivity = activityEvents.slice(0, 8);

  return (
    <Layout>
      <PageContainer>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0f172a] dark:text-slate-100 tracking-tight">
            {t.dashboardTitle}
          </h1>
          <p className="text-[#475569] dark:text-slate-400 mt-1">
            {t.dashboardSubtitle}
          </p>
        </div>

        {/* 1. Visão Geral de Segurança */}
        <section>
          <h2 className="text-base font-semibold text-[#0f172a] dark:text-slate-300 mb-4">
            {t.securityOverview}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 flex items-start gap-4" hover>
              <div className="flex-shrink-0 w-12 h-12 rounded-[10px] bg-[#0ea5e9]/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-[#475569] dark:text-slate-400">{t.humanRiskScore}</p>
                <p className={`text-3xl font-bold mt-1 ${getRiskScoreColor(humanRiskScore)}`}>
                  {humanRiskScore} <span className="text-xl font-normal text-[#475569] dark:text-slate-400">/ 100</span>
                </p>
              </div>
            </Card>

            <Card className="p-6 flex items-start gap-4" hover>
              <div className="flex-shrink-0 w-12 h-12 rounded-[10px] bg-[#0ea5e9]/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-[#475569] dark:text-slate-400">{t.simulationsExecuted}</p>
                <p className="text-3xl font-bold text-[#0f172a] dark:text-slate-100 mt-1">{totalSimulations}</p>
              </div>
            </Card>

            <Card className="p-6 flex items-start gap-4" hover>
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-red-500/15 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{t.vulnerabilitiesDetected}</p>
                <p className="text-3xl font-bold text-[#0f172a] dark:text-slate-100 mt-1">{vulnerabilidadesCount}</p>
              </div>
            </Card>
          </div>
        </section>

        {/* 2. Análise de Risco */}
        <section>
          <h2 className="text-base font-semibold text-[#0f172a] dark:text-slate-300 mb-4">
            {t.riskAnalysis}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { label: t.phishingRisk, pct: phishingRiskPct },
              { label: t.socialEngineeringRisk, pct: seRiskPct },
              { label: t.authorityFraudRisk, pct: authorityRiskPct },
            ].map(({ label, pct }) => {
              const level = getRiskLevelFromScore(100 - pct);
              return (
                <Card
                  key={label}
                  className="p-5"
                  hover
                >
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{label}</p>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`risk-badge px-3 py-1 rounded-full text-xs font-semibold border ${RISK_BADGE_STYLES[level]}`}>
                      {RISK_LABELS[level]}
                    </span>
                    <span className="text-sm font-medium text-[#475569] dark:text-slate-400">{pct}%</span>
                  </div>
                  <div className="h-2 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 transition-all duration-500 ease-out"
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        {/* 3. Activity Timeline & 4. Quick Actions - side by side on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="overflow-hidden h-full">
              <CardHeader title={t.recentActivity} subtitle={t.recentActivitySubtitle} />
              <div className="p-6">
                {recentActivity.length === 0 ? (
                  <EmptyState
                    icon={<svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    title={t.noRecentActivity}
                    description={t.runSimulationsToSee}
                    action={
                      <Button variant="primary" onClick={() => navigate(`/org/${organizationId}/simulations`)}>
                        {t.goToSimulations}
                      </Button>
                    }
                  />
                ) : (
                  <div className="relative">
                    <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-slate-200 dark:bg-slate-700 rounded-full" />
                    <ul className="space-y-0">
                      {recentActivity.map((evt, i) => (
                        <li key={i} className="relative flex gap-4 pb-5 last:pb-0 animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                          <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-[#0ea5e9]/15 border-2 border-[#0ea5e9]/40 flex items-center justify-center">
                            {evt.icon === 'simulation' ? (
                              <svg className="w-3 h-3 text-[#0ea5e9]" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <div className="w-1.5 h-1.5 rounded-full bg-[#0ea5e9]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 pt-0.5">
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                              {formatTime(evt.time, locale)}
                            </p>
                            <p className="text-slate-700 dark:text-slate-300 text-sm">
                              {evt.icon === 'step'
                                ? getStepLabel(evt.type, evt.step)
                                : evt.label}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Card>
          </div>

          <div>
            <Card className="overflow-hidden h-full">
              <CardHeader title={t.quickActions} subtitle={t.quickActionsSubtitle} />
              <div className="p-6 space-y-3">
                <button
                  onClick={() => navigate(`/org/${organizationId}/simulations/phishing-intro`)}
                  className="w-full py-3 px-4 text-left rounded-[10px] border border-[#e2e8f0] dark:border-slate-700 hover:border-[#0ea5e9]/40 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition duration-200 text-sm font-medium text-[#0f172a] dark:text-slate-300"
                >
                  {t.startPhishingSim}
                </button>
                <button
                  onClick={() => navigate(`/org/${organizationId}/simulations/social-engineering-intro`)}
                  className="w-full py-3 px-4 text-left rounded-[10px] border border-[#e2e8f0] dark:border-slate-700 hover:border-[#0ea5e9]/40 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition duration-200 text-sm font-medium text-[#0f172a] dark:text-slate-300"
                >
                  {t.startSocialEngineeringSim}
                </button>
                <button
                  onClick={() => navigate(`/org/${organizationId}/simulations?q=brute`)}
                  className="w-full py-3 px-4 text-left rounded-[10px] border border-[#e2e8f0] dark:border-slate-700 hover:border-[#0ea5e9]/40 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition duration-200 text-sm font-medium text-[#0f172a] dark:text-slate-300"
                >
                  {t.startBruteForceSim}
                </button>
              </div>
            </Card>
          </div>
        </div>

        {/* Vulnerability & Assessments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="overflow-hidden" hover>
            <CardHeader title={t.criticalVulnerabilities} />
            <div className="p-6">
              {data?.criticalVulnerabilities?.length > 0 ? (
                <ul className="space-y-3">
                  {data.criticalVulnerabilities.map((v) => {
                    const titleTranslated =
                      v.title?.startsWith(VULN_TITLE_PREFIX_PT) ?
                        (() => {
                          const questionPart = v.title.slice(VULN_TITLE_PREFIX_PT.length);
                          const qKey = VULN_QUESTION_TO_KEY[questionPart];
                          return t.vulnCriticalRiskPrefix + (qKey && t[qKey] ? t[qKey] : questionPart);
                        })() : v.title;
                    const recTranslated = v.recommendation === VULN_RECOMMENDATION_PT
                      ? t.vulnRecommendationDefault
                      : v.recommendation;
                    return (
                    <li key={v.id} className="p-4 bg-red-100 dark:bg-red-500/15 rounded-lg border border-red-200 dark:border-red-500/30">
                      <p className="font-medium text-red-800 dark:text-red-400">{titleTranslated}</p>
                      {recTranslated && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{recTranslated}</p>
                      )}
                    </li>
                  );
                  })}
                </ul>
              ) : (
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {t.noCriticalVulns}
                </p>
              )}
            </div>
          </Card>

          <Card className="overflow-hidden" hover>
            <CardHeader
              title={t.recentAssessments}
              subtitle={`${t.lastAssessment}: ${data?.lastAssessmentAt ? formatDateTime(data.lastAssessmentAt, locale) : t.none}`}
            />
            <div className="p-6">
              {data?.recentAssessments?.length > 0 ? (
                <div className="space-y-3">
                  {data.recentAssessments.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center justify-between py-2 border-b border-slate-200 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-slate-700 dark:text-slate-300">{a.score} / 100</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {(() => {
                            const d = parseApiDate(a.createdAt);
                            return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(locale) : '';
                          })()}
                        </p>
                      </div>
                      <span className={`risk-badge px-3 py-1 rounded-full text-xs font-semibold border ${RISK_BADGE_STYLES[a.riskLevel] || 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/15 dark:text-slate-400 dark:border-slate-500/30'}`}>
                        {RISK_LABELS[a.riskLevel] || a.riskLevel}
                      </span>
                      <Link
                        to={`/org/${organizationId}/report/${a.id}`}
                        className="text-[#0ea5e9] hover:text-[#0284c7] dark:text-[#0ea5e9] dark:hover:text-[#38bdf8] text-sm font-medium transition duration-200"
                      >
                        {t.viewReport}
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {t.noAssessments}
                </p>
              )}
            </div>
          </Card>
        </div>
      </PageContainer>
    </Layout>
  );
}
